import { CT, RAD, FS } from "../lib/tokens";

/* Phase 10 MF7 — toast colour convention. The renderer colours by `type`
   (success=green / warning=amber / error=red) and prepends its own glyph.
   Many call sites pass a ⚠-prefixed message with no type, which previously
   fell through to the green "success" default. Strip any leading glyph and,
   when type is unset, derive it (⚠→warning, ✕/❌→error, ✓→success). */
/* 160 §5 — THE TOAST'S REAL CALL SHAPE, NAMED. Two prop declarations typed it
   `(m: string) => void`, which is narrower than what `makeToast` returns and
   narrower than what its callers already pass: the SECOND argument picks the
   colour, and `ToastHost` below renders error red, warning amber and success
   green from it. A call site that needed an error toast therefore could not say
   so without a cast — and a cast is how a wrong colour becomes permanent. The
   glyph arm above is the same fact from the other side: it exists because call
   sites were smuggling the kind in as a "⚠ " prefix.
   Widened, not cast (rule 4). `makeToast`'s own parameters are `any`, so its
   return is assignable to this without any change to it. */
export type ToastKind = "success" | "warning" | "error";
export type ToastAction = { label?: string; onClick?: () => void };
export type ToastFn = (
  m?: string,
  type?: ToastKind,
  opts?: { action?: ToastAction | null },
) => void;

/* 209 §25 [Laurent] — « When recording time entries or documents, one does not
   have the time to read the toast with the result: should be 10 seconds. »
   EVERY TOAST STAYS TEN SECONDS: the 3 s default and the 10 s an action bought
   are one length now, and `duration` is gone from the options — the eight
   callers that asked for less (6, 8, 9 s) no longer can. ONE CLOCK PER TOAST,
   AND IT CLOSES ITS OWN: the clock that stood here set the toast to null when
   it rang, whatever toast was up by then — a toast shown during another's time
   was cut short by the other's clock. Each toast is now its own object, and its
   clock closes it only if it is still the one on screen. A click on a toast
   closes it (ToastHost, below; its action button acts first). */
export const TOAST_MS = 10000;

export const makeToast = (setActiveToast: any) => {
  const toast = (m?: any, type?: any, opts?: any) => {
    let _m = m,
      _ty = type;
    if (typeof _m === "string") {
      const _lead = /^([⚠✕✓❌])\s*/.exec(_m);
      if (_lead) {
        _m = _m.slice(_lead[0].length);
        if (!_ty)
          _ty =
            _lead[1] === "⚠"
              ? "warning"
              : _lead[1] === "✓"
                ? "success"
                : "error";
      }
    }
    const mine = { m: _m, type: _ty || "success", action: opts?.action || null };
    setActiveToast(mine);
    setTimeout(() => setActiveToast((cur: unknown) => (cur === mine ? null : cur)), TOAST_MS);
  };
  return toast;
};

export const ToastHost = ({ activeToast, setActiveToast }: any) =>
  activeToast && (
          <div
            className="toast"
            /* 209 §25 — a click closes it; the action button below acts first (its
               own handler runs, then this one). */
            onClick={() => setActiveToast(null)}
            style={{
              cursor: "pointer",
              background:
                activeToast.type === "error"
                  ? "var(--rd)"
                  : activeToast.type === "warning"
                    ? "var(--am)"
                    : "var(--gn)",
            }}
          >
            {activeToast.type === "error" ? "✕ " : activeToast.type === "warning" ? "⚠ " : "✓ "}
            {activeToast.m}
            {activeToast.action && (
              <button
                type="button"
                onClick={() => {
                  const fn = activeToast.action.onClick;
                  setActiveToast(null);
                  if (typeof fn === "function") setTimeout(fn, 0);
                }}
                style={{
                  marginLeft: 10,
                  padding: "3px 10px",
                  fontSize: FS.s10,
                  fontWeight: 600,
                  background: "rgba(255,255,255,.22)",
                  color: CT.c_fff,
                  border: "1px solid rgba(255,255,255,.5)",
                  borderRadius: RAD.r4,
                  cursor: "pointer",
                }}
              >
                {activeToast.action.label}
              </button>
            )}
          </div>
  );
